import Box from "@mui/material/Box";

import type { TimeCheckResult, TimeCheckSuggestion } from "@/libs/tournamentSetupApi";

import { worstBand } from "./time-check-card";
import { TermHelp } from "./term-help";
import { bandSoft, bandText, BandBar, DenseButton, MONO_FONT, Mono, STAFF_FONT, tone } from "./ui";

const BACKGROUND = { plenty: tone.brandNote, tight: tone.warnSoft, wont_fit: tone.badSoft } as const;
const EXPLAIN = { plenty: tone.brandNoteText, tight: tone.warnNoteText, wont_fit: tone.badNoteText } as const;

/** The Events tab's time check with ranked fixes (F2). Applying a fix sends the
 * suggestion's own command payload; the client never edits fields itself. */
export function TimeCheckBar({
  result,
  onApply,
  onEditAssumptions,
  pending,
}: {
  result: TimeCheckResult;
  onApply: (suggestion: TimeCheckSuggestion) => void;
  onEditAssumptions: () => void;
  pending?: boolean;
}) {
  const worst = worstBand(result);
  const short = result.assumptions_line.replace(/ · \d+ min changeover/, "");
  return (
    <Box
      aria-label="Time check"
      component="section"
      sx={{ p: "8px 14px", borderBottom: `1px solid ${tone.divider}`, bgcolor: BACKGROUND[worst], display: "flex", flexDirection: "column", gap: "6px" }}
    >
      <Box sx={{ display: "flex", gap: "14px", alignItems: "center", fontSize: 12.5, flexWrap: "wrap" }}>
        <b>
          Time check <TermHelp label="How the time check works" term="court_hours" />
        </b>
        {result.days.map((day) => (
          <Box component="span" key={day.play_date} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <b>{day.label}</b>
            <BandBar band={day.band} height={6} percent={day.percent} width={80} />
            <Mono sx={{ fontWeight: 700, color: bandText[day.band] }}>
              {day.used_court_hours} of {day.capacity_court_hours} · {day.percent_label}%
            </Mono>
            <Box component="span" sx={{ fontWeight: 600, color: bandText[day.band] }}>
              {day.band_label}
            </Box>
          </Box>
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Box component="span" sx={{ color: tone.subtle }}>
          {short} ·{" "}
          <DenseButton onClick={onEditAssumptions} sx={{ fontSize: 12 }} variant="link">
            Edit
          </DenseButton>
        </Box>
      </Box>
      {result.explanation ? (
        <Box sx={{ fontSize: 12, color: EXPLAIN[worst] }}>{result.explanation}</Box>
      ) : null}
      {result.suggestions.length ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: tone.subtle }}>
            WAYS TO MAKE IT FIT, BEST FIRST
          </Box>
          {result.suggestions.map((suggestion) => (
            <Box
              component="button"
              disabled={pending}
              key={suggestion.key}
              onClick={() => onApply(suggestion)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 26,
                px: "10px",
                border: `1px solid ${suggestion.best ? tone.brandHi : tone.line}`,
                borderRadius: "6px",
                bgcolor: suggestion.best ? tone.brandNote : tone.surface,
                font: "inherit", fontWeight: 600, fontSize: 12.5,
                color: tone.ink2,
                textAlign: "left",
                cursor: "pointer",
                width: "100%",
                boxSizing: "border-box",
                "&:disabled": { opacity: 0.6 },
              }}
              type="button"
            >
              <span>{suggestion.label}</span>
              <Box sx={{ flexGrow: 1 }} />
              {suggestion.days.map((day) => (
                <Box
                  component="span"
                  key={day.play_date}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: 20,
                    px: "6px",
                    borderRadius: "5px",
                    font: `700 11.5px ${MONO_FONT}`,
                    letterSpacing: "-.3px",
                    bgcolor: bandSoft[day.band],
                    color: bandText[day.band],
                  }}
                >
                  {day.label} {day.percent_label}%
                </Box>
              ))}
              <Box component="span" sx={{ fontSize: 11.5, width: 150, textAlign: "right", color: bandText[suggestion.outcome] }}>
                {suggestion.outcome_label}
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
